import { useLanguage } from '@/app/providers';
import { useUsersList } from '@/features/users/usersList/viewmodel/useUsersList';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useRecentChats } from '@/features/chats/model/recentChat';
import { getLocalUserId } from '@/shared/utils';

/**
 * Hook to fetch and combine Recent Chats and Contacts for the Forward feature.
 * @param {string} searchText - The search query.
 */
export const useForwardData = () => {
   const { t } = useLanguage();
   // 1. Fetch Recent Chats
   const { chats: recentChats, loading: loadingRecentChats } = useRecentChats({ scope: 'forward' });

   // 2. Fetch Users (Contacts)
   const {
      data: contactUsers,
      loading: loadingContacts,
      loadMore: loadMoreContacts,
      hasMore: hasMoreContacts,
      searchText,
      handleSearch,
   } = useUsersList({ omitUserIds: recentChats.map(chat => chat.fromUserId) });

   const userInfoMap = useSelector(state => state.userInfoData?.users);
   const groupParticipantsMap = useSelector(state => state.groupParticipantsData.participants);
   const localUserId = getLocalUserId();

   // 3. Filter Recent Chats based on searchText
   const { recentOneToOnes, recentGroups } = useMemo(() => {
      const lowerSearch = searchText.toLowerCase();
      const filtered = searchText
         ? recentChats.filter(chat => {
              const nickName = userInfoMap?.[chat.fromUserId]?.nickName || '';
              return nickName.toLowerCase().includes(lowerSearch);
           })
         : recentChats;

      const mapped = filtered.map(chat => ({
         userId: chat.fromUserId,
         chatType: chat.chatType,
      }));

      return {
         recentOneToOnes: mapped.filter(c => c.chatType !== 'groupchat'),
         recentGroups: mapped.filter(c => {
            if (c.chatType !== 'groupchat') return false;
            const participants = groupParticipantsMap[c.userId];
            // If participants list is loaded, ensure the local user is in it.
            // If it's not loaded yet, default to including it (or hide it if preferred).
            // Usually, recent chats that appear with "You left" shouldn't allow forwarding.
            return participants ? participants.some(p => p.userId === localUserId) : true;
         }),
      };
   }, [recentChats, searchText, userInfoMap, groupParticipantsMap, localUserId]);

   // 4. Combine into Sections
   const sections = useMemo(() => {
      const result = [];

      if (recentOneToOnes.length > 0) {
         result.push({
            title: t('chat.RECENT_CHATS'),
            data: recentOneToOnes,
         });
      }

      if (recentGroups.length > 0) {
         result.push({
            title: t('chat.GROUPS'),
            data: recentGroups,
         });
      }

      if (contactUsers?.length > 0) {
         result.push({
            title: t('chat.CONTACTS'),
            data: contactUsers,
         });
      }

      return result;
   }, [recentOneToOnes, recentGroups, contactUsers, t]);

   return {
      sections,
      loading: loadingRecentChats || loadingContacts,
      loadMoreContacts,
      hasMoreContacts,
      searchText,
      handleSearch,
   };
};
